import type { ResolvedToolDefinition, ToolContentListItem } from "@/data/tools";
import Link from "next/link";
import ToolIcon from "./ToolIcon";
import ToolSectionHeader from "./ToolSectionHeader";

type ToolUseCasesSectionProps = {
  tool: ResolvedToolDefinition;
};

function ContentList({
  items,
  accent,
}: {
  items: readonly ToolContentListItem[];
  accent: "primary" | "accent";
}) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((item) => {
        const itemAccent = item.accent ?? accent;
        const content = (
          <>
            {/* Soft background glow for a natural feel */}
            <div
              className={`absolute -right-4 -top-4 h-24 w-24 rounded-full blur-3xl transition-opacity duration-500 opacity-20 group-hover:opacity-40 ${
                itemAccent === "accent" ? "bg-[#fb397d]" : "bg-[#5b32b4]"
              }`}
            />

            <div className="relative flex items-start gap-4">
              {item.icon ? (
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl transition-transform duration-500 group-hover:scale-110 ${
                    itemAccent === "accent"
                      ? "bg-[#fff0f6] text-[#c3003a]"
                      : "bg-[#f3f0ff] text-[#5b32b4]"
                  }`}
                >
                  <ToolIcon
                    icon={item.icon}
                    size={20}
                    className="stroke-[2px]"
                  />
                </div>
              ) : null}

              <div className="flex flex-col gap-1">
                <h3 className="text-[14px] font-semibold tracking-tight text-[#2b1d3a]">
                  {item.label}
                </h3>
                {item.description ? (
                  <p className="text-[13px] leading-snug text-gray-700 antialiased">
                    {item.description}
                  </p>
                ) : null}
              </div>
            </div>
          </>
        );

        const className = `group relative overflow-hidden rounded-2xl border p-4 transition-all duration-500 hover:shadow-xl hover:shadow-gray-200/50 ${
          itemAccent === "accent"
            ? "border-pink-50 bg-white hover:border-[#fb397d]/20"
            : "border-purple-50 bg-white hover:border-[#5b32b4]/20"
        }`;

        return item.href ? (
          <Link key={item.label} href={item.href} className={className}>
            {content}
          </Link>
        ) : (
          <article
            key={item.label}
            className={className}
          >
            {content}
          </article>
        );
      })}
    </div>
  );
}

export default function ToolUseCasesSection({
  tool,
}: ToolUseCasesSectionProps) {
  const useCases = tool.pageContent?.useCases ?? [];

  if (!useCases.length) {
    return null;
  }

  return (
    <section
      className="max-w-5xl mx-auto py-12 px-4"
      aria-labelledby="use-cases-heading"
    >
      <div className="mb-8">
        <ToolSectionHeader
          headingId="use-cases-heading"
          title="Practical Use Cases"
          description="Real-world workflows where this tool shines."
        />
      </div>
      <ContentList items={useCases} accent="primary" />
    </section>
  );
}
